import { supabase } from './supabase';

/**
 * Database setup utility for running migrations and initial setup
 */
export class DatabaseSetup {
  /**
   * Check if user_preferences table exists
   */
  static async checkUserPreferencesTable(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .select('id')
        .limit(1);
      
      return !error;
    } catch {
      return false;
    }
  }

  /**
   * Create user preference record for the current user
   */
  static async createUserPreference(selectedPlayerId: number): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          selected_player_id: selectedPlayerId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      return !error;
    } catch (error) {
      console.error('Error creating user preference:', error);
      return false;
    }
  }

  /**
   * Get user's selected player preference
   */
  static async getUserPreference(): Promise<number | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return null;
      }

      const { data, error } = await supabase
        .from('user_preferences')
        .select('selected_player_id')
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        return null;
      }

      return data.selected_player_id;
    } catch (error) {
      console.error('Error getting user preference:', error);
      return null;
    }
  }

  /**
   * Update user's selected player preference
   */
  static async updateUserPreference(selectedPlayerId: number): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('user_preferences')
        .update({
          selected_player_id: selectedPlayerId,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      return !error;
    } catch (error) {
      console.error('Error updating user preference:', error);
      return false;
    }
  }

  /**
   * Test database functions are available
   */
  static async testDatabaseFunctions(): Promise<{
    userPreferences: boolean;
    leagueStats: boolean;
    bowlers: boolean;
    functions: boolean;
  }> {
    const results = {
      userPreferences: false,
      leagueStats: false,
      bowlers: false,
      functions: false
    };

    try {
      // Test user_preferences table
      const { error: prefError } = await supabase
        .from('user_preferences')
        .select('id')
        .limit(1);
      results.userPreferences = !prefError;

      // Test league_bowler_week_stats table
      const { error: statsError } = await supabase
        .from('league_bowler_week_stats')
        .select('bowler_id')
        .limit(1);
      results.leagueStats = !statsError;

      // Test bowlers table
      const { error: bowlersError } = await supabase
        .from('bowlers')
        .select('bowler_id')
        .limit(1);
      results.bowlers = !bowlersError;

      // Test database functions by calling one
      const { error: funcError } = await supabase
        .rpc('calculate_league_average_excluding_player', {
          excluded_bowler_id: null,
          season_year: new Date().getFullYear()
        });
      results.functions = !funcError;

    } catch (error) {
      console.error('Error testing database setup:', error);
    }

    return results;
  }

  /**
   * Get setup status and recommendations
   */
  static async getSetupStatus(): Promise<{
    isReady: boolean;
    missing: string[];
    recommendations: string[];
  }> {
    const testResults = await this.testDatabaseFunctions();
    const missing: string[] = [];
    const recommendations: string[] = [];

    if (!testResults.userPreferences) {
      missing.push('user_preferences table');
      recommendations.push('Run migration 001_create_user_preferences.sql');
    }

    if (!testResults.leagueStats) {
      missing.push('league_bowler_week_stats table access');
      recommendations.push('Run migration 002_create_rls_policies.sql');
    }

    if (!testResults.bowlers) {
      missing.push('bowlers table access');
      recommendations.push('Ensure bowlers table exists and RLS policies are configured');
    }

    if (!testResults.functions) {
      missing.push('database functions');
      recommendations.push('Run migration 003_create_league_functions.sql');
    }

    return {
      isReady: missing.length === 0,
      missing,
      recommendations
    };
  }
}